package com.patronat.top.features.dashboard.presentation.ui

import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.patronat.top.dto.DashboardStatsResponse
import com.patronat.top.dto.UserRole
import com.patronat.top.features.dashboard.presentation.mappers.buildDashboardCards
import com.patronat.top.features.dashboard.presentation.model.DashboardStatCardUi
import com.patronat.top.features.dashboard.presentation.model.DashboardStatMetricUi
import com.patronat.top.ui.organisms.EmptyStateContent
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.White
import com.patronat.top.ui.theme.rememberAppFontFamily
import org.jetbrains.compose.resources.painterResource
import top.features.dashboard.generated.resources.Res
import top.features.dashboard.generated.resources.img_empty_statistic

private val DashboardCardBorder = Color(0xFFF2EDE2)

@Composable
fun DashboardStatsSection(
    role: UserRole,
    dashboardStats: DashboardStatsResponse?,
    isLoading: Boolean
) {
    if (isLoading && dashboardStats == null) {
        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            CircularProgressIndicator()
        }
        return
    }

    val cards = remember(role, dashboardStats) {
        buildDashboardCards(role, dashboardStats)
    }

    if (cards.isEmpty()) {
        EmptyStateContent(
            image = painterResource(Res.drawable.img_empty_statistic),
            title = "Статистика поки недоступна",
            description = "Спробуйте оновити сторінку трохи пізніше.",
            buttonText = "",
            onButtonClick = {},
            buttonIcon = AppIcons.Plus,
            buttonNeeded = false
        )
        return
    }

    LazyVerticalGrid(
        columns = GridCells.Adaptive(minSize = 360.dp),
        modifier = Modifier.fillMaxSize(),
        verticalArrangement = Arrangement.spacedBy(16.dp),
        horizontalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        items(items = cards, key = { it.id }) { card ->
            DashboardStatCard(card = card)
        }
    }
}

@Composable
private fun DashboardStatCard(card: DashboardStatCardUi) {
    val shape = RoundedCornerShape(12.dp)
    val fontFamily = rememberAppFontFamily()

    Surface(
        shape = shape,
        tonalElevation = 0.dp,
        shadowElevation = 0.dp,
        color = White,
        modifier = Modifier
            .fillMaxWidth()
            .height(145.dp)
            .border(width = 1.dp, color = DashboardCardBorder, shape = shape)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 15.dp, vertical = 11.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                Surface(
                    shape = RoundedCornerShape(8.dp),
                    color = DashboardCardBorder,
                    modifier = Modifier.border(
                        width = 1.dp,
                        color = DashboardCardBorder,
                        shape = RoundedCornerShape(8.dp)
                    )
                ) {
                    Icon(
                        painter = card.icon(),
                        contentDescription = null,
                        tint = Color(0xFF2C2C2C),
                        modifier = Modifier
                            .size(36.dp)
                            .padding(8.dp)
                    )
                }

                Text(
                    text = card.title,
                    style = TextStyle(
                        fontFamily = fontFamily,
                        fontWeight = FontWeight.Medium,
                        fontSize = 14.sp,
                        lineHeight = 20.sp,
                        color = Color(0xFF2C2C2C)
                    )
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                card.metrics.forEach { metric ->
                    DashboardMetricItem(
                        metric = metric,
                        modifier = Modifier.weight(1f),
                        fontFamily = fontFamily
                    )
                }
            }
        }
    }
}

@Composable
private fun DashboardMetricItem(
    metric: DashboardStatMetricUi,
    modifier: Modifier,
    fontFamily: FontFamily
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(
            text = metric.label,
            style = TextStyle(
                fontFamily = fontFamily,
                fontWeight = FontWeight.Medium,
                fontSize = 14.sp,
                lineHeight = 20.sp,
                color = Color(0xFF6D6D6D)
            )
        )

        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            Text(
                text = metric.value,
                style = TextStyle(
                    fontFamily = fontFamily,
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 20.sp,
                    lineHeight = 28.sp,
                    color = Color(0xFF2C2C2C)
                )
            )

            metric.percentage?.let { percentage ->
                Text(
                    text = percentage,
                    style = TextStyle(
                        fontFamily = fontFamily,
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 12.sp,
                        lineHeight = 16.sp,
                        color = Color(0xFF007A55)
                    )
                )
            }
        }
    }
}
